/**
 * Pure helpers for Create Feature click-to-edit (US-108778).
 *
 * Kept free of React/Cesium so the non-blocking pick path and stale-response
 * guard can be unit-tested without mounting the full modal.
 */
import type { DrawGeometryType, DrawnGeometry } from '../../../hooks/useCreateFeatureDraw';

/** GeoJSON-ish geometry payload returned by getFeatureAt. */
export type PickedGeoJson = {
  type: string;
  coordinates: unknown;
};

/**
 * Reduce a picked GeoJSON geometry (possibly Multi*) to a single editable
 * Point/LineString/Polygon for the chosen family.
 */
export function normalizePickedGeometry(geom: PickedGeoJson | null | undefined, family: DrawGeometryType): DrawnGeometry | null {
  if (!geom || !geom.coordinates) return null;
  const isMulti = geom.type.toLowerCase().startsWith('multi');
  if (family === 'Point') {
    const c = isMulti ? (geom.coordinates as unknown[])[0] : geom.coordinates;
    return Array.isArray(c) ? { type: 'Point', coordinates: c as number[] } : null;
  }
  if (family === 'Line') {
    const c = isMulti ? (geom.coordinates as unknown[])[0] : geom.coordinates;
    return Array.isArray(c) ? { type: 'LineString', coordinates: c as number[][] } : null;
  }
  // Polygon — coordinates is [ring, …]; for MultiPolygon take the first polygon.
  const poly = isMulti ? (geom.coordinates as unknown[])[0] : geom.coordinates;
  return Array.isArray(poly) ? { type: 'Polygon', coordinates: poly as number[][][] } : null;
}

export type FeatureAtPayload = {
  gid?: number | null;
  geometry?: PickedGeoJson | null;
  properties?: Record<string, unknown> | null;
};

export type ApplyFeaturePickInput = {
  /** Generation stamped when the request was fired. */
  requestGen: number;
  /** Latest generation (bumped on every new pick). Stale if !== requestGen. */
  currentGen: number;
  feature: FeatureAtPayload | null | undefined;
  geometryType: DrawGeometryType;
  /** Schema field names allowed into attribute values. */
  schemaFields: string[];
};

export type ApplyFeaturePickResult =
  | { applied: false; reason: 'stale' | 'miss' | 'bad-geometry' }
  | {
      applied: true;
      gid: number;
      geometry: DrawnGeometry;
      attributeValues: Record<string, unknown>;
    };

/**
 * Decide whether a getFeatureAt response should open edit mode.
 * Returns `applied: false` for stale generations, empty hits, or unusable geometry
 * so a slow network reply cannot override a newer click or a finished draw.
 */
export function applyFeaturePickResult(input: ApplyFeaturePickInput): ApplyFeaturePickResult {
  if (input.requestGen !== input.currentGen) {
    return { applied: false, reason: 'stale' };
  }
  const feature = input.feature;
  if (!feature?.gid) {
    return { applied: false, reason: 'miss' };
  }
  const geometry = normalizePickedGeometry(feature.geometry ?? null, input.geometryType);
  if (!geometry) {
    return { applied: false, reason: 'bad-geometry' };
  }
  const schemaKeys = new Set(input.schemaFields);
  const attributeValues: Record<string, unknown> = {};
  Object.entries(feature.properties ?? {}).forEach(([k, v]) => {
    if (schemaKeys.has(k)) attributeValues[k] = v;
  });
  return {
    applied: true,
    gid: Number(feature.gid),
    geometry,
    attributeValues,
  };
}
